import React, { FC, useCallback, useMemo, useState } from 'react';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { Alert, Button, Form, Input, Modal, Popconfirm, Tooltip } from 'antd';
import {
	getDisplayableErrorMessage,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import {
	getCancellationBlockedReason,
	getCancellationBlockingServiceCall,
} from '../../utils/WorkOrderUtils';
import moment from 'moment';
import {
	cancelWorkOrderForBuyer,
	cancelWorkOrderForSupplier,
} from '../../thunks/work_orders_thunks';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';

const CancelWorkOrder: FC<any> = ({
	workOrder,
	cancelButton,
	refresh = () => {},

	userType,
	companyConfig,
	cancelWorkOrder,
	currentUser,
}): React.ReactElement => {
	const [form] = Form.useForm();

	const [showCancelModal, setShowCancelModal] = useState(false);
	const [buttonLoading, setButtonLoading] = useState(false);
	const [cancelError, setCancelError] = useState('');

	const canShowCancelButton = useMemo(
		() =>
			userType === 'buyer'
				? nullSafeGetOrElse('config.newServiceRequest.allowCancel', companyConfig, false)
				: true, //supplier configuration handled in page (to allow differentiation between third party and internal)
		[companyConfig, userType]
	);

	// Disable the affordance up front when a technician is checked in and the buyer has the lock
	// turned on. The backend enforces the same rule on write and stays the authority, so
	// handleCancelWorkOrder still surfaces its 409 for anything only the server can see.
	const blockingServiceCall = useMemo(
		() => getCancellationBlockingServiceCall(workOrder, userType, companyConfig),
		[companyConfig, userType, workOrder]
	);

	const rawButton = cancelButton ? (
		{
			...cancelButton,
			props: {
				...cancelButton.props,
				disabled: !!blockingServiceCall || nullSafeGet('props.disabled', cancelButton),
				// A disabled antd Button swallows hover, so the Tooltip below listens on the wrapper
				// span instead and the button must not intercept the pointer.
				style: blockingServiceCall
					? { ...nullSafeGetOrElse('props.style', cancelButton, {}), pointerEvents: 'none' }
					: nullSafeGet('props.style', cancelButton),
				onClick: blockingServiceCall ? undefined : () => setShowCancelModal(true),
			},
		}
	) : (
		<Popconfirm
			onConfirm={() => setShowCancelModal(true)}
			placement="topLeft"
			title="Are you sure you want to cancel?"
			okText="Yes"
			cancelText="No"
			disabled={!!blockingServiceCall}
		>
			<Button
				type="default"
				key="cancel"
				style={{
					marginBottom: 16,
					marginRight: 16,
					...(blockingServiceCall ? { pointerEvents: 'none' as const } : {}),
				}}
				ghost={true}
				disabled={!!blockingServiceCall}
			>
				Cancel
			</Button>
		</Popconfirm>
	);

	const customButton = blockingServiceCall ? (
		<Tooltip title={getCancellationBlockedReason(blockingServiceCall)} placement="right">
			<span style={{ cursor: 'not-allowed' }}>{rawButton}</span>
		</Tooltip>
	) : (
		rawButton
	);

	const handleCancelWorkOrder = useCallback(
		(values) => {
			const cancelNote = {
				text: values.note,
				noteAddedBy: nullSafeGet('email', currentUser),
				noteAddedAt: moment.utc(),
			};

			setButtonLoading(true);
			setCancelError('');
			cancelWorkOrder({ ...workOrder, note: cancelNote })
				.then(() => {
					setShowCancelModal(false);
					refresh();
				})
				.catch((err) => {
					// The modal deliberately stays open so the reason stays on screen. A 409 from the
					// cancellation lock is a conflict the user has to go act on (get the technician
					// checked out), which a message.error toast would take away after a few seconds.
					setCancelError(getDisplayableErrorMessage(err, 'Failed to cancel work order.'));
				})
				.finally(() => {
					setButtonLoading(false);
				});
		},
		[cancelWorkOrder, currentUser, refresh, workOrder]
	);

	const closeCancelModal = useCallback(() => {
		setShowCancelModal(false);
		setCancelError('');
	}, []);

	return canShowCancelButton ? (
		<AccessPermissionChecker name={PERMISSION_NAMES.CANCEL_WORK_ORDERS}>
			<div>
				<Modal
					title={`${`Cancel`} Work Order`}
					visible={showCancelModal}
					width={600}
					okText="Confirm"
					onCancel={closeCancelModal}
					onOk={form.submit}
					confirmLoading={buttonLoading}
					closable={false}
				>
					<Form
						form={form}
						onFinish={(values) => {
							setButtonLoading(true);
							handleCancelWorkOrder(values);
						}}
					>
						{cancelError ? (
							<Form.Item>
								<Alert message={cancelError} type="error" />
							</Form.Item>
						) : null}
						<Form.Item
							name="note"
							label="Notes"
							rules={[
								{
									required: true,
									message: `Please add a note on why this work order was ${
										userType === `supplier` ? `declined` : `cancelled`
									}.`,
								},
							]}
							required={true}
						>
							<Input.TextArea />
						</Form.Item>
					</Form>
				</Modal>
				{customButton}
			</div>
		</AccessPermissionChecker>
	) : null;
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	fetching: state.work_orders.fetching,
	match: ownProps.match,
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	cancelWorkOrder: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? cancelWorkOrderForSupplier(entity)
				: cancelWorkOrderForBuyer(entity)
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(CancelWorkOrder)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
